import { faroCoreApiClient } from "@api/faro-core-api-client";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { useStellarApiClient } from "@hooks/use-stellar-api-client";
import { useToast } from "@hooks/use-toast";
import { runtimeConfig } from "@src/runtime-config";
import { sentryCaptureError } from "@src/utils/sentry-utils";
import { EmailUtils } from "@stellar/utils";
import { selectedProjectSelector } from "@store/projects/projects-selector";
import {
  defaultProjectLaunchTargetSelector,
  selectedSdbCompanySelector,
} from "@store/sdb-company/sdb-company-selector";
import { useAppSelector } from "@store/store-helper";
import { currentUserSelector } from "@store/user/user-selector";
import { getFeedbackMessage } from "@utils/email-utils";
import { FeedbackModalEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";

interface SendFeedbackProps {
  /**
   * The name of the feature that the feedback is about, e.g.
   * General Feedback or Annotations.
   */
  featureName: string;

  /** Optional message that the user inputs to give feedback */
  feedbackMessage?: string;

  /**
   * Optional rating given by the customer.
   * !: It should be standardized to a 1-5 scale, to generate appropriate Amplitude reports
   */
  selectedRating?: number | null;

  /**
   * Whether the user allows FARO to contact them to follow up on their feedback.
   * @default false
   */
  hasUserAllowedContact?: boolean;
}

interface UseFeedbackHook {
  /**
   * Function to send feedback to the backend and track the event.
   */
  sendFeedback: (props: SendFeedbackProps) => Promise<void>;
}

/**
 * Hook to send feedback event to the backend and to track them.
 * Use the functions here instead of calling the API directly,
 * since some values are automatically filled in.
 */
export function useFeedback(): UseFeedbackHook {
  const selectedCompany = useAppSelector(selectedSdbCompanySelector);
  const selectedProject = useAppSelector(selectedProjectSelector);
  const currentUser = useAppSelector(currentUserSelector);
  const { defaultOpenProjectTarget } = useAppSelector(
    defaultProjectLaunchTargetSelector
  );
  const { showToast } = useToast();
  const { handleErrorWithToast } = useErrorContext();
  const { trackEvent } = useTrackEvent();
  const { stellarApiClient } = useStellarApiClient();

  /**
   * Sends a request to the backend to send a feedback email, and to track the event in Amplitude.
   * This function handles the error and shows a toast if the request fails, therefore does not
   * have to use try/catch since it will always resolve the promise.
   */
  async function sendFeedback({
    featureName,
    feedbackMessage,
    selectedRating,
    hasUserAllowedContact = false,
  }: SendFeedbackProps): Promise<void> {
    const isInternalUser = EmailUtils.isInternalUser(currentUser?.email ?? "");
    const customMessage = getFeedbackMessage({
      selectedRating,
      feedbackMessage,
      defaultOpenProjectTarget,
      hasUserAllowedContact,
      isInternalUser,
    });

    const subject = `SDB Feedback: ${featureName}`;
    trackEvent({
      name: FeedbackModalEvents.submitFeedback,
      props: {
        topic: subject,
        rating: selectedRating?.toString() ?? "",
        didProvideFeedback: Boolean(feedbackMessage),
        defaultOpenProjectTarget,
        hasUserAllowedContact: hasUserAllowedContact.toString(),
      },
    });

    async function sendFeedbackToFeedbackAPI(): Promise<void> {
      try {
        await stellarApiClient.feedback.submitFeedback({
          application: "SXG Dashboard",
          appVersion: runtimeConfig.appVersion,
          feedbackMessage: feedbackMessage ?? "",
          featureName,
          selectedRating: selectedRating ?? undefined,
          companyId: selectedCompany?.id,
          userId: currentUser?.id,
          workspaceRole: selectedCompany?.role.company,
          defaultOpenProjectTarget,
          hasUserAllowedContact: hasUserAllowedContact,
          isInternalUser,
        });
      } catch (error) {
        // Failing to call the feedback route should not block the user from using the app
        // Use Sentry to log the error
        sentryCaptureError({ error, title: "Error calling feedback route" });
      }
    }

    async function sendFeedbackToCoreAPI(): Promise<void> {
      try {
        await faroCoreApiClient.notification.notificationRequest({
          payload: {
            type: "feedback",
            context: {
              companyId: selectedCompany?.id,
              projectId: selectedProject?.id,
            },
            data: {
              subject,
              name: "Sphere-Dashboard",
              comment: customMessage,
            },
          },
        });
        showToast({
          message: "Your request has been received.",
          description:
            "Thank you for reaching out, we appreciate your feedback.",
          type: "success",
        });
      } catch (error) {
        handleErrorWithToast({
          id: `sendFeedback-${Date.now().toString()}`,
          title: "Error sending feedback",
          error,
        });
      }
    }
    await Promise.all([sendFeedbackToFeedbackAPI(), sendFeedbackToCoreAPI()]);
  }

  return {
    sendFeedback,
  };
}
